import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "./api";

export type Alert = {
  id: string;
  level: "info" | "warn" | "fault";
  title: string;
  body: string;
  href?: string;
};

export type VolumeView = {
  id: string;
  label: string;
  detail: string;
  /** null = not configured on this host. Never rendered as green or red. */
  ok: boolean | null;
  freeBytes: number;
  totalBytes: number;
  freeText: string;
  totalText: string;
  usedPct: number;
  level: "ok" | "warn" | "fault" | "unknown";
  source: string;
};

export type LibraryView = {
  id: string;
  label: string;
  /** The instance's own web UI. */
  url: string;
  ok: boolean;
  detail: string;
  count: number;
  monitored: number;
  missing: number;
  bytes: number;
  sizeText: string;
  queued: number;
};

export type ServiceView = {
  id: string;
  label: string;
  role: string;
  ok: boolean;
  detail: string;
  url: string;
};

export type HostHealth = {
  name: string;
  role: string;
  host: string;
  /** Web UI on one of the probed ports, when there is one. */
  url?: string;
  ok: boolean | null;
  detail: string;
  latencyMs: number | null;
  model?: string;
  os?: string;
  cpu?: string;
  memGb?: number;
};

export type PlexSession = {
  user: string;
  title: string;
  kind: string;
  player: string;
  state: string;
  transcoding: boolean;
};

export type StatusPayload = {
  ok: boolean;
  mode: string;
  generatedAt: number;
  alerts: Alert[];
  volumes: VolumeView[];
  libraries: LibraryView[];
  services: ServiceView[];
  fleet: HostHealth[];
  downloads: {
    ok: boolean;
    detail: string;
    paused: boolean;
    speedText: string;
    sizeLeftText: string;
    queueCount: number;
    timeLeftText: string;
    jobs: { id: string; title: string; status: string; percent: number; etaText: string }[];
  };
  playback: { ok: boolean; detail: string; version: string; label: string; sessions: PlexSession[] };
  pendingCount: number;
  queueCount: number;
  stuckCount: number;
  recent: { time: string; text: string }[];
  instances: InstancePair[];
};

/** HD vs renamed-4K binary parity for Sonarr/Radarr. */
export type InstancePair = {
  app: string;
  hdVersion: string;
  uhdVersion: string;
  state: "ok" | "drift" | "hd-missing" | "uhd-missing";
  hdListening: boolean;
  uhdListening: boolean;
  uhdExe: string;
};

export type Approval = {
  id: string;
  /** "fixer" = written to pending.json; "queue" = live *arr queue row. */
  source: "fixer" | "queue";
  app?: string;
  /** The owning *arr's queue page. */
  url?: string;
  /** trackedDownloadState etc., verbatim from the instance. */
  state?: string;
  /** statusMessages from the instance — why it is actually blocked. */
  messages?: string[];
  expected?: string;
  title?: string;
  recommended?: string;
  stuckReason?: string;
  outputPath?: string;
  downloadId?: string;
  files?: string[];
};

/**
 * Poll status, but only while the tab is actually visible.
 *
 * v1 polled every 8s forever, including with the phone in a pocket — four *arr
 * catalog fetches per tick, all night. `visibilitychange` costs one listener
 * and stops that entirely.
 */
export function useStatus(intervalMs = 10_000) {
  const [data, setData] = useState<StatusPayload | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/api/status");
      if (res.status === 401) return;
      if (!res.ok) throw new Error(`status ${res.status}`);
      const json = (await res.json()) as StatusPayload;
      if (!alive.current) return;
      setData(json);
      setError("");
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;

    // The very first fetch always fires, even if the tab mounts hidden —
    // otherwise a backgrounded mount shows skeletons forever with nothing
    // to trigger a retry. Only the recurring poll is visibility-gated.
    // Audited 2026-08.
    let first = true;
    const tick = async () => {
      if (first || document.visibilityState === "visible") await load();
      first = false;
      if (alive.current) timer.current = setTimeout(tick, intervalMs);
    };
    void tick();

    const onVisible = () => {
      // Coming back to the app should feel instant, not "wait up to 10s".
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, intervalMs]);

  return { data, error, loading, reload: load };
}

export type StatusContextValue = ReturnType<typeof useStatus>;
